// The sales plan as data: the stages a prospect moves through, the
// two-week contact sequence inside the Working stage, what each step
// asks the rep to do (with the words), the outcomes a rep can log, and
// what each outcome does to the row. SalesPipelineTab and the drawer
// render this; nothing here touches the database.
//
// Sequence (from the cadence research, Oct 2026): call first so the
// email gets opened, the video does the explaining, the visit is about
// fit. Day numbers are the plan's; due dates are computed from when the
// previous step was actually done, so a late step pushes the rest.

import { STAGE_WORDS, lineLabel, nicheOf, withLine } from './salesLines';

export const DAY_MS = 86400000;
export const PARK_DAYS = 60;
// A no on a cold call is usually a reflex, so it parks and comes back.
// Only do-not-contact is forever.
export const CIRCLE_BACK_DAYS = 90;
// A hot store that goes this long without a touch is pulled back into the
// queue even when nothing is due.
export const HOT_STALE_HOURS = 48;

// Placeholders the scripts use. The drawer fills [owner], [store], [rep],
// [phone] from the row and the signed-in rep. [video link] is the demo
// video from VITE_SALES_VIDEO_LINK; until that is set it stays in the text
// and the email composer will not send it (hasUnfilled).
export const VIDEO_LINK_PLACEHOLDER = '[video link]';
export const VIDEO_LINK = String(import.meta.env.VITE_SALES_VIDEO_LINK || '').trim();
/** True while a script still carries a placeholder nobody filled. */
export const hasUnfilled = (text) => /\[(video link|owner|store|rep|phone|time)\]/i.test(String(text || ''));
// [demo line] is Exitium's own phone number, answered by the phone agent
// set up as Exitium's front desk (api/sales-demo-line.py). It comes from
// VITE_SALES_DEMO_LINE; until that is set, every sentence that mentions it
// is dropped from the scripts so nobody sends a number that does not exist.
export const DEMO_LINE_PLACEHOLDER = '[demo line]';
export const DEMO_LINE = String(import.meta.env.VITE_SALES_DEMO_LINE || '').trim();
export const formatPhone = (raw) => {
  const d = String(raw || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
  return d.length === 10 ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}` : String(raw || '');
};
// Outcomes that are not a step of the plan (the webhooks and the demo line write them).
export const EXTRA_OUTCOME_LABEL = { reply_received: 'They replied', demo_line: 'Called the demo line', brief: 'Built the brief', bounced: 'Email bounced', complained: 'Marked as spam', delivered: 'Email delivered' };

export const STAGES = [
  { key: 'new', label: 'New', tone: 'slate', hint: 'On the list. Nobody has reached out yet.' },
  { key: 'working', label: 'Working', tone: 'blue', hint: 'In the two-week sequence.' },
  { key: 'demo', label: 'Demo', tone: 'violet', hint: 'Saw it live on their floor, or booked a time to.' },
  { key: 'pilot', label: 'Pilot', tone: 'amber', hint: '30-day pilot on one showroom, setup fee waived.' },
  { key: 'customer', label: 'Customer', tone: 'emerald', hint: 'Month to month. Ask for two referrals.' },
  { key: 'parked', label: 'Parked', tone: 'zinc', hint: 'Not now. Comes back in 60 days.' },
  { key: 'lost', label: 'Lost', tone: 'red', hint: 'Said no. The reason is on the row.' },
];
export const STAGE_BY_KEY = Object.fromEntries(STAGES.map((s) => [s.key, s]));
// `who` is the store (or its line, 'websites' | 'furniture'): the Pilot
// stage reads Building for a restaurant, and so on (salesLines.js).
export const stageOf = (key, who = '') => {
  const s = STAGE_BY_KEY[key] || STAGE_BY_KEY.new;
  const n = typeof who === 'string' ? who : who ? nicheOf(who) : '';
  const w = n && STAGE_WORDS[n] && STAGE_WORDS[n][key];
  return w ? { ...s, ...w } : s;
};

// Outcome "next" values:
//   advance   finish this step, the next step of the sequence is due
//   hold      stay on this step, due again in `dueDays`
//   demo | pilot | customer | lost | parked   move to that stage
//   restart   back to Working, step 0, due now
//   done      nothing left to do on this row
const O = (key, label, next, extra = {}) => ({ key, label, next, ...extra });

export const CADENCE = [
  {
    key: 'call1',
    day: 1,
    kind: 'call',
    label: 'Call the owner',
    short: 'Call',
    goal: 'Thirty seconds. Owner by name. The call is a name drop, not the pitch: it makes the email get opened. Most will not pick up, that is fine.',
    say: 'Hi, is this [owner]? This is [rep] with Exitium. We built the AI platform East Bay Furniture runs now: their website, their phone line and the assistant on the floor. I am emailing you a two-minute video of it today. Look for East Bay in the subject line.',
    voicemail: 'Hi [owner], [rep] with Exitium. We built the AI platform for East Bay Furniture. I am emailing you a two-minute video of it right now, look for East Bay in the subject line. My number is [phone].',
    outcomes: [
      O('voicemail', 'Left a voicemail', 'advance'),
      O('no_answer', 'No answer, no voicemail', 'advance'),
      O('spoke', 'Spoke. Sending the video', 'advance'),
      O('booked', 'Booked a demo', 'demo', { askDate: 'When is the demo?' }),
      O('callback', 'Call back at a set time', 'hold', { askDate: 'When should you call back?', callback: true }),
      O('bad_number', 'Wrong number', 'hold', { dueDays: 1, ask: 'What number should we try?' }),
      O('not_interested', 'Not interested', 'lost', { circleBack: CIRCLE_BACK_DAYS }),
      O('no_number', 'No phone number. Skip to the email', 'advance'),
    ],
  },
  {
    key: 'video',
    day: 1,
    kind: 'email',
    label: 'Email the East Bay demo video',
    short: 'Video',
    goal: 'Within an hour of the call. The subject line names East Bay. The video is a customer story, not a feature tour.',
    say: 'Subject: The AI platform we built for East Bay Furniture\n\n[owner], this is [rep] with Exitium. I just left you a message.\n\nHere is the two-minute video: [video link]\n\nEast Bay\'s phone is answered around the clock, their website sells on its own, and shoppers on the floor find pieces by voice. I would like to show you on your own floor. Ten minutes.\n\nOr call [demo line] any time and the same assistant that answers for East Bay will answer for us and book the ten minutes.\n\n[rep]\n[phone]',
    outcomes: [
      O('sent', 'Sent', 'advance', { marks: 'video_sent_at' }),
      O('replied', 'They replied, want to talk', 'demo', { marks: 'video_sent_at', askDate: 'When will you talk or visit? Pick the time' }),
      O('no_email', 'No email address found', 'advance'),
    ],
  },
  {
    key: 'dm',
    day: 2,
    kind: 'dm',
    label: 'Send the same link by Facebook or Instagram message',
    short: 'DM',
    goal: 'Furniture owners check their store page more than their inbox. Same video, two lines.',
    say: 'Hi [owner], [rep] with Exitium. We built East Bay Furniture\'s AI platform and I emailed you the two-minute video yesterday. Here it is again: [video link]. Or call [demo line] and hear it answer for us. I will stop by this week to show it on your floor.',
    outcomes: [
      O('sent', 'Sent', 'advance'),
      O('replied', 'They replied, want to talk', 'demo', { askDate: 'When will you talk or visit? Pick the time' }),
      O('no_account', 'No page found', 'advance'),
    ],
  },
  {
    key: 'visit',
    day: 4,
    kind: 'visit',
    label: 'Walk in with the tablet',
    short: 'Visit',
    goal: 'Mid-week, mid-morning, when the floor is slow. Show the East Bay video on the tablet, then the phone agent live on their own number. Ask for ten minutes on their floor, not a meeting.',
    say: 'Hi, I am [rep] with Exitium. I am the one who sent the East Bay Furniture video. Is [owner] in? I would love ten minutes to show it working on your floor.',
    outcomes: [
      O('showed', 'Showed the demo on their floor', 'demo', { marks: 'visited_at', toStep: 2 }),
      O('booked', 'Booked a time to come back', 'demo', { marks: 'visited_at', askDate: 'When are you coming back?' }),
      O('owner_out', 'Owner not there, left a card', 'advance', { marks: 'visited_at' }),
      O('not_interested', 'Not interested', 'lost', { marks: 'visited_at', circleBack: CIRCLE_BACK_DAYS }),
      O('too_far', 'Too far to visit. Skip', 'advance'),
    ],
  },
  {
    key: 'text',
    day: 6,
    kind: 'text',
    label: 'Text follow-up',
    short: 'Text',
    goal: 'Only if they gave you their cell in person. One line plus the link. Never cold text.',
    say: '[owner], [rep] from Exitium. Good meeting you. Here is the East Bay video again: [video link]. Want me to set up a 30-day pilot on your showroom?',
    outcomes: [
      O('sent', 'Sent', 'advance'),
      O('replied', 'They replied, want to talk', 'demo', { askDate: 'When will you talk or visit? Pick the time' }),
      O('no_number', 'No cell number, skipped', 'advance'),
    ],
  },
  {
    key: 'call2',
    day: 9,
    kind: 'call',
    label: 'Second call: ask for the pilot',
    short: 'Call 2',
    goal: 'They have heard the name four times. Ask for the pilot, not another meeting.',
    say: 'Hi [owner], [rep] with Exitium again. You have the East Bay video. The way most stores start is a 30-day pilot on one showroom, month to month, and we waive the setup. Can I get you set up this week?',
    voicemail: 'Hi [owner], [rep] with Exitium. Quick one: most stores start with a 30-day pilot on one showroom, setup waived. I can have you live this week. [phone].',
    outcomes: [
      O('pilot', 'Yes to the pilot', 'pilot'),
      O('booked', 'Booked a demo', 'demo', { askDate: 'When is the demo?' }),
      O('voicemail', 'Left a voicemail', 'advance'),
      O('not_now', 'Spoke. Not now', 'advance'),
      O('callback', 'Call back at a set time', 'hold', { askDate: 'When should you call back?', callback: true }),
      O('not_interested', 'Not interested', 'lost', { circleBack: CIRCLE_BACK_DAYS }),
      O('no_number', 'No phone number. Skip to the email', 'advance'),
    ],
  },
  {
    key: 'email2',
    day: 12,
    kind: 'email',
    label: 'Last email: one result, one question',
    short: 'Email 2',
    goal: 'Short. One thing East Bay got from it, one question. Then the row parks for 60 days.',
    say: 'Subject: One question\n\n[owner], East Bay Furniture\'s phone now answers every call, nights and weekends, and the owner gets the ones that need a person. Is that worth ten minutes on your floor? Call [demo line] and hear it answer for us.\n\n[rep]\n[phone]',
    outcomes: [
      O('sent', 'Sent. Park it', 'parked'),
      O('replied', 'They replied, want to talk', 'demo', { askDate: 'When will you talk or visit? Pick the time' }),
      O('pilot', 'Yes to the pilot', 'pilot'),
    ],
  },
];

// The Demo stage has three steps of its own, kept in p.step like the
// sequence: 0 confirm the booked demo the day before (the reminder is the
// best-evidenced thing in the whole pipeline), 1 run it, 2 ask for the
// pilot. A booked demo carries its time in demo_at.
export const DEMO_STEPS = [
  {
    key: 'demo_confirm',
    kind: 'text',
    label: 'Confirm the demo',
    short: 'Confirm',
    goal: 'The day before. One text with the time and what they will see. Two reminders cut no-shows by about a quarter. If they did not give you a cell, call instead.',
    say: 'Hi [owner], [rep] with Exitium. Looking forward to [time] at [store]. I will bring the tablet and show the East Bay platform live on your floor, ten minutes. Reply here if the time needs to move.',
    outcomes: [
      O('reminded', 'Reminder sent', 'demo_step', { toStep: 1, due: 'demo_at' }),
      O('rebooked', 'They moved it. New time', 'demo_step', { toStep: 0, askDate: 'When is the demo now?' }),
      O('no_number', 'No cell. Called instead', 'demo_step', { toStep: 1, due: 'demo_at' }),
      O('cancelled', 'They cancelled. Try again next week', 'demo_step', { toStep: 0, dueDays: 7, askDate: 'When should we offer? Pick a day to rebook' }),
      O('not_interested', 'Not interested after all', 'lost'),
    ],
  },
  {
    key: 'demo_run',
    kind: 'demo',
    label: 'Run the demo',
    short: 'Demo',
    goal: 'East Bay video on the tablet first, two minutes. Then the phone agent live on their own number. Then ask for the pilot right there.',
    say: 'This is East Bay Furniture, same size as you. Watch what happens when a shopper calls after hours. Now let me call your number and show you what your customers hear today.',
    outcomes: [
      O('pilot', 'Yes to the pilot, right there', 'pilot'),
      O('showed', 'Showed it. They want to think', 'demo_step', { toStep: 2, dueDays: 2, marks: 'visited_at' }),
      O('no_show', 'No-show. Text to rebook now', 'demo_step', { toStep: 0, dueDays: 0, askDate: 'Offer a new time. When?' }),
      O('not_interested', 'Saw it. Not interested', 'lost'),
    ],
  },
  {
    key: 'demo_ask',
    kind: 'call',
    label: 'Ask for the 30-day pilot',
    short: 'Ask',
    goal: 'Within two days of the demo. One showroom, month to month, setup fee waived. Price is on exitiumlabs.com/pricing.',
    say: 'The way to find out is a 30-day pilot on this showroom. Month to month, setup waived. If it does not earn its keep you stop. Can I set you up this week?',
    voicemail: 'Hi [owner], [rep] with Exitium. Following up on the demo. Most stores start with a 30-day pilot, setup waived. I can have you live this week. [phone].',
    outcomes: [
      O('pilot', 'Pilot signed', 'pilot'),
      O('thinking', 'Thinking about it', 'hold', { dueDays: 3 }),
      O('callback', 'Call back at a set time', 'hold', { askDate: 'When should you call back?', callback: true }),
      O('voicemail', 'Left a voicemail', 'hold', { dueDays: 2 }),
      O('another_demo', 'Wants another look. Book it', 'demo_step', { toStep: 0, askDate: 'When is the demo?' }),
      O('not_interested', 'Said no', 'lost'),
    ],
  },
];

// What to do in the stages past the sequence.
export const STAGE_ACTIONS = {
  demo: DEMO_STEPS[2], // the generic entry; currentAction picks the real step

  pilot: {
    kind: 'call',
    label: 'Check in. Ask for two referrals',
    goal: 'Weekly during the pilot. How are the calls going, what did the phone agent catch. Every happy pilot gets asked for two store names.',
    say: 'How is the first week going? Anything the phone agent said that you want changed? Also, who are two owners you know who should see this?',
    dueDays: 7,
    outcomes: [
      O('converted', 'Converted to monthly', 'customer'),
      O('checked', 'Checked in, going well', 'hold', { dueDays: 7 }),
      O('referrals', 'Got referrals (add them as New)', 'hold', { dueDays: 7 }),
      O('cancelled', 'Cancelled the pilot', 'lost'),
    ],
  },
  customer: {
    kind: 'call',
    label: 'Ask for two referrals',
    goal: 'One in five Exitium deals should come from a customer. Ask every customer, every month.',
    say: 'Who are two owners you know who should see this? I will mention your name.',
    dueDays: 14,
    outcomes: [
      O('referrals', 'Got referrals (add them as New)', 'hold', { dueDays: 30 }),
      O('asked', 'Asked, none yet', 'hold', { dueDays: 30 }),
      O('done', 'Nothing more to do', 'done'),
    ],
  },
  parked: {
    kind: 'call',
    label: 'Revisit',
    goal: 'Sixty days later. One call. Anything changed?',
    say: 'Hi [owner], [rep] with Exitium. We talked a couple of months ago about the AI platform East Bay runs. A lot of stores have started since. Worth ten minutes now?',
    dueDays: PARK_DAYS,
    outcomes: [
      O('restart', 'Back in. Start the sequence over', 'restart'),
      O('still_no', 'Still not now. Park again', 'hold', { dueDays: PARK_DAYS }),
      O('not_interested', 'Not interested', 'lost'),
    ],
  },
  lost: null,
};

// A store owner wrote back. The inbound webhook (api/sales-email-inbound.py)
// sets next_action to this label and next_due_at to now, so the store jumps
// to the top of Today. The rep answers from the card; "answered" puts the
// row back on whatever step it was on. The label is the signal: no column.
export const REPLY_ACTION_LABEL = 'Reply to their email';
export const REPLY_ACTION = {
  key: 'reply',
  kind: 'email',
  label: REPLY_ACTION_LABEL,
  short: 'Reply',
  goal: 'They wrote back. Answer today, in their words, one question at the end. A reply answered the same day is the warmest lead on the list.',
  say: '[owner], thanks for getting back to me.\n\nTen minutes on your floor is the fastest way to see it on your own phone line and your own stock. Does this week work?\n\n[rep]\n[phone]',
  outcomes: [
    O('answered', 'Answered. Back on the plan', 'resume'),
    O('booked', 'They want to talk. Book it', 'demo', { askDate: 'When will you talk or visit? Pick the time' }),
    O('pilot', 'Yes to the pilot', 'pilot'),
    O('not_now', 'Not now. Park it', 'parked'),
    O('not_interested', 'Not interested', 'lost'),
  ],
};

export const KIND_LABEL = {
  call: 'Call',
  voicemail: 'Voicemail',
  email: 'Email',
  dm: 'Message',
  visit: 'Visit',
  text: 'Text',
  demo: 'Demo',
  note: 'Note',
  stage: 'Stage',
  assign: 'Assigned',
};

/** The step of the sequence a New/Working row is on. */
export const stepIndexOf = (p) => {
  const i = Number(p?.step ?? 0);
  return Math.min(Math.max(Number.isFinite(i) ? i : 0, 0), CADENCE.length - 1);
};

/**
 * What the rep should do next on this row, from the plan. Returns null
 * for Lost and for rows with nothing left to do.
 * { label, kind, goal, say, voicemail, outcomes, stepIndex, stepTotal, isSequence }
 */
export function currentAction(p) {
  if (!p) return null;
  if (p.next_action === REPLY_ACTION_LABEL && p.stage !== 'lost' && !isDoNotContact(p)) {
    return withLine({ ...REPLY_ACTION, isSequence: false, isReply: true, resume: planAction(p) }, nicheOf(p));
  }
  return planAction(p);
}

/** The step the plan has the row on, ignoring a pending reply. */
export function planAction(p) {
  if (!p) return null;
  const stage = p.stage || 'new';
  if (stage === 'new' || stage === 'working') {
    const i = stepIndexOf(p);
    const s = CADENCE[i];
    return withLine({ ...s, stepIndex: i, stepTotal: CADENCE.length, isSequence: true }, nicheOf(p));
  }
  if (stage === 'demo') {
    const i = Math.min(Math.max(Number(p.step) || 0, 0), DEMO_STEPS.length - 1);
    return withLine({ ...DEMO_STEPS[i], stepIndex: i, stepTotal: DEMO_STEPS.length, isSequence: false, isDemo: true }, nicheOf(p));
  }
  const a = STAGE_ACTIONS[stage];
  if (!a) return null;
  if (stage === 'customer' && !p.next_due_at && p.next_action === null) return null;
  return withLine({ ...a, key: `${stage}_action`, isSequence: false }, nicheOf(p));
}

/** Due date for a demo step: the demo itself, the day before it, or in n days. */
const demoDue = (nowIso, demoAtIso, step, dueDays) => {
  const now = new Date(nowIso).getTime();
  if (dueDays != null) return addDays(nowIso, dueDays);
  const at = demoAtIso ? new Date(demoAtIso).getTime() : NaN;
  if (!Number.isFinite(at)) return nowIso;
  if (step === 0) return new Date(Math.max(now, at - DAY_MS)).toISOString(); // confirm the day before
  if (step === 1) return new Date(Math.max(now, at)).toISOString(); // run it when it happens
  return addDays(nowIso, 2);
};

const addDays = (fromIso, days) => new Date(new Date(fromIso).getTime() + days * DAY_MS).toISOString();

/**
 * The row changes an outcome causes. Pure. `nowIso` is when the touch
 * happened. Returns { patch, touch } where patch is the sales_prospects
 * update and touch is the sales_touches row (without ids).
 */
export function applyOutcome(p, outcome, { nowIso = new Date().toISOString(), note = '', rep = null, when = null, never = false } = {}) {
  const action = currentAction(p);
  const fromStage = p.stage || 'new';
  const niche = nicheOf(p);
  const lab = (step, key) => lineLabel(step, key, niche);
  const patch = {
    last_touch_at: nowIso,
    touch_count: (p.touch_count || 0) + 1,
    updated_at: nowIso,
  };
  if (outcome?.marks && !p[outcome.marks]) patch[outcome.marks] = nowIso;

  const setStage = (stage, dueDays) => {
    patch.stage = stage;
    const a = STAGE_ACTIONS[stage];
    patch.next_action = a ? lab(a, `${stage}_action`) : null;
    patch.next_due_at = a ? addDays(nowIso, dueDays ?? a.dueDays) : null;
  };

  switch (outcome?.next) {
    case 'advance': {
      const i = action?.isSequence ? action.stepIndex : 0;
      const nxt = CADENCE[i + 1];
      if (nxt) {
        patch.stage = 'working';
        patch.step = i + 1;
        patch.next_action = lab(nxt, nxt.key);
        const gap = Math.max(0, nxt.day - CADENCE[i].day);
        patch.next_due_at = gap === 0 ? nowIso : addDays(nowIso, gap);
      } else {
        setStage('parked');
        patch.parked_until = patch.next_due_at;
      }
      break;
    }
    case 'hold': {
      const days = outcome.dueDays ?? 1;
      patch.next_due_at = when || addDays(nowIso, days);
      if (outcome.callback && when) patch.next_action = callbackLabel(when);
      if (fromStage === 'new') patch.stage = 'working';
      if (fromStage === 'parked') patch.parked_until = patch.next_due_at;
      break;
    }
    case 'demo': {
      // Into the Demo stage: a booked time lands on step 0 (confirm the day
      // before); a demo that already happened lands on step 2 (ask).
      const step = outcome.toStep != null ? outcome.toStep : 0;
      patch.stage = 'demo';
      patch.step = step;
      if (when) patch.demo_at = when;
      else if (step === 2 && !p.demo_at) patch.demo_at = nowIso;
      patch.next_action = lab(DEMO_STEPS[step], DEMO_STEPS[step].key);
      patch.next_due_at = demoDue(nowIso, patch.demo_at || p.demo_at, step, step === 2 ? 2 : (when ? null : 1));
      break;
    }
    case 'demo_step': {
      const step = outcome.toStep != null ? outcome.toStep : 2;
      patch.stage = 'demo';
      patch.step = step;
      if (when) patch.demo_at = when;
      patch.next_action = lab(DEMO_STEPS[step], DEMO_STEPS[step].key);
      patch.next_due_at = demoDue(nowIso, patch.demo_at || p.demo_at, step, outcome.due === 'demo_at' ? null : outcome.dueDays != null ? outcome.dueDays : (when ? null : 1));
      break;
    }
    case 'pilot':
      setStage('pilot');
      if (!p.pilot_at) patch.pilot_at = nowIso;
      break;
    case 'customer':
      setStage('customer');
      if (!p.won_at) patch.won_at = nowIso;
      break;
    case 'parked':
      setStage('parked');
      patch.parked_until = patch.next_due_at;
      break;
    case 'lost':
      if (outcome.circleBack && !never) {
        // Parked, not lost: the row comes back on its own.
        setStage('parked', outcome.circleBack);
        patch.parked_until = patch.next_due_at;
        if (note) patch.lost_reason = note;
        break;
      }
      patch.stage = 'lost';
      patch.next_action = null;
      patch.next_due_at = null;
      if (note) patch.lost_reason = note;
      break;
    case 'resume': {
      // Answered their email: back to the step the plan had them on. A new
      // row that replied is working now. The due date is the plan's gap from
      // the step before, counted from today.
      const a = action?.resume || planAction({ ...p, next_action: null });
      if (fromStage === 'new') patch.stage = 'working';
      if (a?.isSequence) {
        const i = a.stepIndex;
        patch.next_action = lab(CADENCE[i], CADENCE[i].key);
        const gap = i > 0 ? Math.max(0, CADENCE[i].day - CADENCE[i - 1].day) : 0;
        patch.next_due_at = gap === 0 ? nowIso : addDays(nowIso, gap);
      } else if (a?.isDemo) {
        patch.next_action = lab(DEMO_STEPS[a.stepIndex], DEMO_STEPS[a.stepIndex].key);
        patch.next_due_at = demoDue(nowIso, p.demo_at, a.stepIndex, a.stepIndex === 2 ? 2 : null);
      } else if (a) {
        patch.next_action = a.label;
        patch.next_due_at = addDays(nowIso, a.dueDays ?? 2);
      } else {
        patch.next_action = null;
        patch.next_due_at = null;
      }
      break;
    }
    case 'restart':
      patch.stage = 'working';
      patch.step = 0;
      patch.next_action = lab(CADENCE[0], CADENCE[0].key);
      patch.next_due_at = nowIso;
      patch.parked_until = null;
      break;
    case 'done':
      patch.next_action = null;
      patch.next_due_at = null;
      break;
    default:
      break;
  }

  const temp = TEMP_BY_OUTCOME[outcome?.key];
  if (temp && !never) {
    const tags = withTemp(p.tags, patch.stage === 'parked' || patch.stage === 'lost' ? 'cold' : temp);
    if (tags.join('|') !== (p.tags || []).join('|')) patch.tags = tags;
  }

  // Called back when you said you would: marked in the note, scored by the game.
  const due = p.next_due_at ? new Date(p.next_due_at).getTime() : NaN;
  const now = new Date(nowIso).getTime();
  const keptCallback = isCallback(p) && now >= due - 15 * 60000 && now <= due + 30 * 60000;
  const touchNote = [keptCallback ? CALLBACK_KEPT_NOTE : '', outcome?.callback && when ? callbackLabel(when) : '', note].filter(Boolean).join(' · ');
  const touch = {
    kind: action?.kind || 'note',
    outcome: outcome?.key || null,
    note: touchNote || null,
    from_stage: fromStage,
    to_stage: patch.stage || fromStage,
    step: action?.isSequence || action?.isDemo ? action.stepIndex : null,
    happened_at: nowIso,
    rep_id: rep?.id || null,
    rep_name: rep?.name || null,
  };
  return { patch, touch };
}

/** A brand-new row: first call due now. */
export function freshProspectFields(nowIso = new Date().toISOString()) {
  return {
    stage: 'new',
    step: 0,
    next_action: CADENCE[0].label,
    next_due_at: nowIso,
    touch_count: 0,
  };
}

/** Moving a row by hand (the drawer's stage picker). */
export function moveToStage(p, stage, nowIso = new Date().toISOString()) {
  const patch = { stage, updated_at: nowIso };
  const niche = nicheOf(p);
  if (stage === 'new' || stage === 'working') {
    patch.step = stage === 'new' ? 0 : stepIndexOf(p);
    patch.next_action = lineLabel(CADENCE[patch.step], CADENCE[patch.step].key, niche);
    patch.next_due_at = nowIso;
    patch.parked_until = null;
  } else if (stage === 'lost') {
    patch.next_action = null;
    patch.next_due_at = null;
  } else {
    const a = STAGE_ACTIONS[stage];
    patch.next_action = a ? lineLabel(a, `${stage}_action`, niche) : null;
    patch.next_due_at = a ? addDays(nowIso, a.dueDays ?? 2) : null;
    if (stage === 'parked') patch.parked_until = patch.next_due_at;
    if (stage === 'demo') { patch.step = 2; patch.next_action = lineLabel(DEMO_STEPS[2], DEMO_STEPS[2].key, niche); patch.next_due_at = addDays(nowIso, 2); if (!p.demo_at) patch.demo_at = nowIso; }
    if (stage === 'pilot' && !p.pilot_at) patch.pilot_at = nowIso;
    if (stage === 'customer' && !p.won_at) patch.won_at = nowIso;
  }
  return patch;
}

/** Fill [owner] [store] [rep] [phone] in a script. */
export function fillScript(text, { prospect, rep } = {}) {
  if (!text) return '';
  const owner = prospect?.owner_name?.trim() || '';
  const d = String(rep?.phone || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
  const phone = d.length === 10 ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}` : rep?.phone || 'your number';
  const at = prospect?.demo_at ? new Date(prospect.demo_at) : null;
  const time = at && !isNaN(at) ? at.toLocaleString(undefined, { weekday: 'long', hour: 'numeric', minute: '2-digit' }) : 'our time';
  const withLine = DEMO_LINE
    ? text.replace(/\[demo line\]/g, formatPhone(DEMO_LINE))
    : text.replace(/[^.!?\n]*\[demo line\][^.!?\n]*[.!?]?/g, '').replace(/\.(?=[A-Z])/g, '. ').replace(/[ \t]{2,}/g, ' ').replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
  // No owner name yet: ask for the owner instead of "Hi, is this there?"
  const named = owner ? withLine : withLine
    .replace(/is this \[owner\]\?/g, 'is the owner in?')
    .replace(/Is \[owner\] in\?/g, 'Is the owner in?')
    .replace(/Hi \[owner\],/g, 'Hi,')
    .replace(/^\[owner\], /gm, 'Hi, ')
    .replace(/\[owner\]/g, 'there');
  const video = VIDEO_LINK ? named.replace(/\[video link\]/g, VIDEO_LINK) : named;
  return video
    .replace(/\[time\]/g, time)
    .replace(/\[owner\]/g, owner)
    .replace(/\[store\]/g, prospect?.name || 'your store')
    .replace(/\[city\]/g, prospect?.city || 'town')
    .replace(/\[rep\]/g, rep?.name || 'your name')
    .replace(/\[phone\]/g, phone);
}

/** sms: link with the body filled in. The "?&body=" form works on iOS and Android. */
export const smsLink = (tel, body) => (tel ? `sms:${tel}${body ? `?&body=${encodeURIComponent(body)}` : ''}` : '');

// ---------- due-date helpers ----------
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

/** 'overdue' | 'today' | 'soon' (within 7 days) | 'later' | 'none' */
export function dueBucket(iso, now = new Date()) {
  if (!iso) return 'none';
  const due = new Date(iso);
  if (isNaN(due)) return 'none';
  const today = startOfDay(now);
  const dueDay = startOfDay(due);
  if (dueDay < today) return 'overdue';
  if (dueDay.getTime() === today.getTime()) return 'today';
  if (dueDay.getTime() - today.getTime() <= 7 * DAY_MS) return 'soon';
  return 'later';
}

export function dueLabel(iso, now = new Date()) {
  const b = dueBucket(iso, now);
  if (b === 'none') return '';
  const due = new Date(iso);
  const days = Math.round((startOfDay(due) - startOfDay(now)) / DAY_MS);
  if (b === 'overdue') return days === -1 ? 'Yesterday' : `${-days} days late`;
  if (b === 'today') return 'Today';
  if (days === 1) return 'Tomorrow';
  return due.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

// A store that said, clearly, never again. Stays on the list as Lost so an
// import cannot bring it back, and the drawer says so in red.
export const DO_NOT_CONTACT = 'do not contact';
export const isDoNotContact = (p) => Array.isArray(p?.tags) && p.tags.includes(DO_NOT_CONTACT);

export const isOpenStage = (stage) => stage === 'new' || stage === 'working' || stage === 'demo' || stage === 'pilot';

// ---------- temperature ----------
// Hot / warm / cold lives in tags as 'hot' | 'warm' | 'cold', after the
// import area tag (read the area with areaOf, not tags[0]). Each
// outcome suggests one; the rep can change it with one tap.
export const TEMPS = [
  { key: 'hot', label: 'Hot', hint: 'Wants to talk. Touch within two days, every time.' },
  { key: 'warm', label: 'Warm', hint: 'A real conversation, no yes yet.' },
  { key: 'cold', label: 'Cold', hint: 'No answer, or not now.' },
];
const TEMP_KEYS = new Set(TEMPS.map((t) => t.key));
export const TEMP_BY_OUTCOME = {
  booked: 'hot', replied: 'hot', pilot: 'hot', showed: 'hot', reminded: 'hot', rebooked: 'hot', another_demo: 'hot', converted: 'hot', restart: 'warm',
  spoke: 'warm', not_now: 'warm', callback: 'warm', thinking: 'warm', owner_out: 'warm', answered: 'hot', still_no: 'cold',
  no_answer: 'cold', voicemail: 'cold', not_interested: 'cold', cancelled: 'cold', no_show: 'warm',
};
// Research tier rides in tags too: 'tier:A' | 'tier:B' | 'tier:C'.
const TIER_RE = /^tier:([ABC])$/i;
const isMetaTag = (t) => TEMP_KEYS.has(t) || TIER_RE.test(t) || t === DO_NOT_CONTACT;

export const tempOf = (p) => {
  if (p?.next_action === REPLY_ACTION_LABEL && p?.stage !== 'lost') return 'hot';
  return (p?.tags || []).find((t) => TEMP_KEYS.has(t)) || '';
};
export const withTemp = (tags, temp) => {
  const rest = (Array.isArray(tags) ? tags : []).filter((t) => !TEMP_KEYS.has(t));
  return temp ? [...rest, temp] : rest;
};
export const tierOf = (p) => { const t = (p?.tags || []).find((x) => TIER_RE.test(x)); return t ? t.slice(5).toUpperCase() : ''; };
export const tierTag = (raw) => { const m = String(raw || '').trim().match(/^(?:tier\s*:?\s*)?([ABC])\b/i); return m ? `tier:${m[1].toUpperCase()}` : ''; };
/** The import area: the first tag that is not a temperature, tier or do-not-contact. */
export const areaOf = (p) => (p?.tags || []).find((t) => !isMetaTag(t)) || '';

/** Hot, open, and nobody has touched it in HOT_STALE_HOURS. */
export const isGoingCold = (p, now = new Date()) => {
  if (tempOf(p) !== 'hot' || !isOpenStage(p?.stage)) return false;
  const last = p.last_touch_at ? new Date(p.last_touch_at).getTime() : 0;
  return now.getTime() - last > HOT_STALE_HOURS * 3600000;
};

// ---------- call-backs ----------
// A promised call-back rides in next_action ("Call back · Thu 10:30 AM")
// with next_due_at at that minute. The queue holds it back until the time
// comes, then puts it first.
export const CALLBACK_PREFIX = 'Call back · ';
export const CALLBACK_LEAD_MS = 10 * 60000;
export const CALLBACK_KEPT_NOTE = 'Called back on time';
export const callbackLabel = (iso) => `${CALLBACK_PREFIX}${new Date(iso).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`;
export const isCallback = (p) => typeof p?.next_action === 'string' && p.next_action.startsWith(CALLBACK_PREFIX);

// ---------- the store's local time ----------
// Reps call from California into other time zones. One zone per state is
// close enough to know whether the store is open (a few states split; the
// bigger half wins).
const STATE_ZONE = {
  CT: 'America/New_York', DE: 'America/New_York', DC: 'America/New_York', FL: 'America/New_York', GA: 'America/New_York', IN: 'America/New_York', KY: 'America/New_York', ME: 'America/New_York', MD: 'America/New_York', MA: 'America/New_York', MI: 'America/New_York', NH: 'America/New_York', NJ: 'America/New_York', NY: 'America/New_York', NC: 'America/New_York', OH: 'America/New_York', PA: 'America/New_York', RI: 'America/New_York', SC: 'America/New_York', VT: 'America/New_York', VA: 'America/New_York', WV: 'America/New_York',
  AL: 'America/Chicago', AR: 'America/Chicago', IL: 'America/Chicago', IA: 'America/Chicago', KS: 'America/Chicago', LA: 'America/Chicago', MN: 'America/Chicago', MS: 'America/Chicago', MO: 'America/Chicago', NE: 'America/Chicago', ND: 'America/Chicago', OK: 'America/Chicago', SD: 'America/Chicago', TN: 'America/Chicago', TX: 'America/Chicago', WI: 'America/Chicago',
  CO: 'America/Denver', ID: 'America/Denver', MT: 'America/Denver', NM: 'America/Denver', UT: 'America/Denver', WY: 'America/Denver', AZ: 'America/Phoenix',
  CA: 'America/Los_Angeles', NV: 'America/Los_Angeles', OR: 'America/Los_Angeles', WA: 'America/Los_Angeles', AK: 'America/Anchorage', HI: 'Pacific/Honolulu',
};
// Panhandle Florida and El Paso run an hour behind their state.
const CITY_ZONE = { pensacola: 'America/Chicago', 'fort walton beach': 'America/Chicago', 'panama city': 'America/Chicago', destin: 'America/Chicago', 'el paso': 'America/Denver' };
export const zoneOf = (p) => CITY_ZONE[String(p?.city || '').trim().toLowerCase()] || STATE_ZONE[String(p?.state || '').trim().toUpperCase()] || '';
/** "1:05 PM their time" plus whether a showroom is likely open (10 to 7, every day). Null when the zone is ours or unknown. */
export function storeClock(p, now = new Date()) {
  const zone = zoneOf(p);
  if (!zone) return null;
  const mine = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const fmt = (opts) => new Intl.DateTimeFormat('en-US', { timeZone: zone, ...opts }).format(now);
  if (fmt({ hour: 'numeric', minute: '2-digit' }) === new Intl.DateTimeFormat('en-US', { timeZone: mine, hour: 'numeric', minute: '2-digit' }).format(now)) return null;
  const hour = Number(fmt({ hour: 'numeric', hourCycle: 'h23' }));
  return { text: fmt({ hour: 'numeric', minute: '2-digit' }), open: hour >= 10 && hour < 19 };
}

// ---------- where am I, and where does each answer go ----------
// The rep should never have to work out whether today is a first touch or
// a follow-up, or what pressing a button will do. phaseOf says where the
// store is in plain words; whereItGoes runs the outcome on a copy of the
// row and says what would happen, so every button can carry its own
// "then" line. Both are pure.

export const SEQUENCE_DAYS = CADENCE[CADENCE.length - 1].day;
const fmtWhen = (iso) => new Date(iso).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const fmtDay = (iso) => new Date(iso).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
const daysSince = (iso, now) => (iso ? Math.floor((now.getTime() - new Date(iso).getTime()) / DAY_MS) : null);
const PAST_KIND = { call: 'Called', email: 'Emailed', dm: 'Messaged', visit: 'Walked in', text: 'Texted', demo: 'Demoed' };

/**
 * { title, detail, tone, first } for the row. `touches` (this store's, any
 * order) sharpen the detail: what the last touch was and how long ago.
 */
export function phaseOf(p, touches = [], now = new Date()) {
  if (!p) return null;
  const stage = p.stage || 'new';
  const web = nicheOf(p) === 'websites';
  const last = [...touches].filter((t) => t.kind !== 'note' && t.kind !== 'stage' && t.kind !== 'assign').sort((a, b) => new Date(b.happened_at) - new Date(a.happened_at))[0] || null;
  const lastText = last ? `${PAST_KIND[last.kind] || 'Touched'} ${daysSince(last.happened_at, now) === 0 ? 'today' : daysSince(last.happened_at, now) === 1 ? 'yesterday' : `${daysSince(last.happened_at, now)} days ago`}` : '';
  if (isDoNotContact(p)) return { title: 'Do not contact', detail: 'They were clear. Nobody calls, emails or visits.', tone: 'red' };
  if (stage === 'lost') return { title: 'Lost', detail: p.lost_reason || 'They said no.', tone: 'red' };
  if (p.next_action === REPLY_ACTION_LABEL) return { title: 'They wrote back', detail: 'Answer today. A reply answered the same day is the warmest lead on the list.', tone: 'emerald' };
  if (isCallback(p)) return { title: 'Call-back you promised', detail: `${p.next_action.replace(CALLBACK_PREFIX, 'You said ')}. Calling on the minute is worth extra points.`, tone: 'amber' };
  if (stage === 'new' || stage === 'working') {
    const i = stepIndexOf(p);
    const step = CADENCE[i];
    if (i === 0 && !(p.touch_count > 0)) return { title: 'First reach-out', detail: web ? 'Nobody has talked to this place yet. Call first, then the Drizzled site goes out within the hour.' : 'Nobody has talked to this store yet. Call first, then the email goes out within the hour.', tone: 'blue', first: true };
    const prev = CADENCE[i - 1];
    const gap = last ? daysSince(last.happened_at, now) : null;
    const noReply = prev && (prev.kind === 'email' || prev.kind === 'text' || prev.kind === 'dm') && gap != null && gap >= 1 ? ` No reply in ${gap} day${gap === 1 ? '' : 's'}, which is normal: the plan's answer is this step.` : '';
    return { title: `Follow-up ${i} of ${CADENCE.length - 1}`, detail: `Day ${step.day} of the ${SEQUENCE_DAYS}-day plan.${lastText ? ` ${lastText}.` : ''}${noReply}`, tone: 'blue' };
  }
  if (stage === 'demo') {
    const i = Math.min(Math.max(Number(p.step) || 0, 0), DEMO_STEPS.length - 1);
    const at = p.demo_at ? fmtWhen(p.demo_at) : 'a time not set yet';
    if (i === 0) return { title: 'Demo booked', detail: `${at}. A reminder goes out by itself the day before and the morning of, with a calendar invite. If they have not confirmed by the morning, call.`, tone: 'violet' };
    if (i === 1) return { title: 'Demo day', detail: `${at}. ${web ? 'Show their menu on their phone, then ask for the yes right there.' : 'Show it, then ask for the pilot right there.'}`, tone: 'violet' };
    return { title: 'After the demo', detail: `They saw it${p.demo_at ? ` on ${fmtDay(p.demo_at)}` : ''}. ${web ? 'Ask for the yes within two days.' : 'Ask for the pilot within two days.'}`, tone: 'violet' };
  }
  if (stage === 'pilot') { const w = p.pilot_at ? Math.max(1, Math.floor(daysSince(p.pilot_at, now) / 7) + 1) : 1; return web ? { title: `Building, week ${w}`, detail: 'Menu, photos, hours and the domain login. One message a week until it is live.', tone: 'amber' } : { title: `Pilot, week ${w}`, detail: 'Check in every week. Every happy pilot gets asked for two store names.', tone: 'amber' }; }
  if (stage === 'customer') return { title: 'Customer', detail: web ? 'Site live, $197 a month. Ask for two referrals every month.' : 'Month to month. Ask for two referrals every month.', tone: 'emerald' };
  if (stage === 'parked') return { title: 'Parked', detail: `${p.parked_until ? `Comes back by itself on ${fmtDay(p.parked_until)}.` : 'Comes back by itself.'}${lastText ? ` ${lastText}.` : ''} One call: anything changed?`, tone: 'zinc' };
  return { title: stageOf(stage).label, detail: stageOf(stage).hint, tone: 'slate' };
}

/** One line: what logging `outcome` on this row would do. */
export function whereItGoes(p, outcome, now = new Date()) {
  if (!p || !outcome) return '';
  if (outcome.next === 'lost') return outcome.circleBack ? `Parks ${outcome.circleBack} days, comes back by itself` : 'Lost. Stays on the list so an import cannot bring it back';
  if (outcome.callback) return 'Comes back to the top of your list at the minute you pick';
  if (outcome.askDate && (outcome.next === 'demo' || (outcome.next === 'demo_step' && outcome.toStep === 0))) return 'Demo stage. Reminders and the calendar invite go out by themselves';
  if (outcome.askDate) return 'Due again at the time you pick';
  if (outcome.ask) return 'Stays here, due tomorrow';
  const { patch } = applyOutcome(p, outcome, { nowIso: now.toISOString() });
  const stage = patch.stage || p.stage || 'new';
  if (stage === 'lost') return 'Lost';
  if (!patch.next_action && patch.next_action !== undefined) return 'Nothing more to do on this store';
  const label = patch.next_action !== undefined ? patch.next_action : (currentAction(p)?.label || p.next_action || '');
  const due = patch.next_due_at || p.next_due_at;
  const days = due ? Math.round((new Date(due).getTime() - now.getTime()) / DAY_MS) : null;
  const when = days == null ? '' : days <= 0 ? 'right away' : days === 1 ? 'tomorrow' : `in ${days} days`;
  if (outcome.next === 'hold' && (patch.stage || p.stage) === (p.stage || 'new')) return `Stays on this step, due again ${when || 'soon'}`;
  if (stage === 'parked') return `Parks. Comes back ${when || 'later'} to revisit`;
  if (stage === 'demo') return `Demo stage. Next: ${label}${when ? `, ${when}` : ''}`;
  if (stage === 'pilot') return nicheOf(p) === 'websites' ? `Building. First check-in ${when}` : `Pilot. Weekly check-in starts ${when}`;
  if (stage === 'customer') return `Customer. Referral ask ${when}`;
  if (patch.step != null && (stage === 'working' || stage === 'new') && patch.step !== (p.step || 0)) return `Next: day ${CADENCE[patch.step].day}, ${CADENCE[patch.step].short || CADENCE[patch.step].label}${when ? `, ${when}` : ''}`;
  if (patch.step === 0 && stage === 'working' && outcome.next === 'restart') return 'Back to the first call, due now';
  return label ? `Next: ${label}${when ? `, ${when}` : ''}` : 'Logged';
}

// The two reminders the system sends by itself for a booked demo
// (api/cron/sales-reminders.py has the same words in Python). The owner
// already said yes to the time, so this is a confirmation, not a cold text.
export const REMINDER_SCRIPTS = {
  invite: { label: 'Calendar invite', when: 'as soon as the demo is booked' },
  day_before: { label: 'Day-before confirmation', when: 'about 24 hours before', say: 'Hi [owner], [rep] with Exitium. I have heard back from everyone else on tomorrow\'s list, so I wanted to get your confirmation too: [time] at [store], ten minutes on your phone. Still good? Reply yes, or send a better time.' },
  morning_of: { label: 'Morning-of reminder', when: 'a few hours before', say: 'Hi [owner], [rep] with Exitium. Looking forward to [time] today at [store]. Everyone else today is lined up. See you then.' },
};
export const AUTO_OUTCOMES = { invite_sent: 'Calendar invite sent', auto_reminder_day_before: 'Day-before confirmation sent', auto_reminder_morning: 'Morning-of reminder sent', auto_skipped: 'Reminder skipped' };

/** Google Calendar "add this" link for the rep's own calendar. */
export function calendarLink(p, me) {
  if (!p?.demo_at) return '';
  const start = new Date(p.demo_at);
  if (isNaN(start)) return '';
  const end = new Date(start.getTime() + 30 * 60000);
  const z = (d) => d.toISOString().replace(/[-:]|\.\d{3}/g, '');
  const q = new URLSearchParams({ action: 'TEMPLATE', text: `Exitium demo: ${p.name}`, dates: `${z(start)}/${z(end)}`, details: `Ten minutes on their phone.${p.owner_name ? ` Owner: ${p.owner_name}.` : ''}${p.phone ? ` ${p.phone}.` : ''}${me?.name ? ` Rep: ${me.name}.` : ''}`, location: [p.address, p.city, p.state].filter(Boolean).join(', ') });
  return `https://calendar.google.com/calendar/render?${q}`;
}
